import { useMemo, useState } from "react"
import type { ColumnDef } from "@tanstack/react-table"
import { ArrowDownTrayIcon, EnvelopeIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Layout } from "@/components/Layout"
import { CommsTabs } from "@/components/CommsTabs"
import { DataTable } from "@/components/DataTable/DataTable"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { Button } from "@/components/Button"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { useToast } from "@/components/Toast"
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import type { WaitlistResponse, WaitlistSignup } from "@/hooks/useComms"

function formatSignupTime(value: string): string {
  const sqliteUtc = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? `${value.replace(" ", "T")}Z`
    : value
  const date = new Date(sqliteUtc)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

export interface WaitlistPageProps {
  /** Page heading. */
  title: string
  /** What the addresses were collected for, in the retention note. */
  purpose: string
  /** Product word, used in the empty state and the delete dialog. */
  product: string
  /** CSV filename prefix. */
  csvPrefix: string
  /** Name of the list in the delete confirmation. */
  listName: string
  useList: () => UseQueryResult<WaitlistResponse, Error>
  getCsv: () => Promise<Blob>
  useDelete: () => UseMutationResult<{ deleted: true }, Error, string>
}

export function WaitlistPage({
  title,
  purpose,
  product,
  csvPrefix,
  listName,
  useList,
  getCsv,
  useDelete,
}: WaitlistPageProps) {
  const { toast } = useToast()
  const { data, isLoading, error } = useList()
  const deleteSignup = useDelete()
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<WaitlistSignup | null>(null)

  const columns = useMemo<ColumnDef<WaitlistSignup>[]>(
    () => [
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <a className="font-medium text-blue-600 hover:underline" href={`mailto:${row.original.email}`}>
            {row.original.email}
          </a>
        ),
      },
      { accessorKey: "source", header: "Source" },
      {
        accessorKey: "created_at",
        header: "Joined",
        cell: ({ row }) => formatSignupTime(row.original.created_at),
      },
      {
        id: "delete",
        header: "",
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            disabled={deleteSignup.isPending}
            onClick={() => setPendingDelete(row.original)}
            title={`Delete ${row.original.email} from the waitlist`}
            aria-label={`Delete ${row.original.email} from the waitlist`}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [deleteSignup.isPending],
  )

  const exportCsv = async () => {
    setIsExporting(true)
    setExportError(null)
    try {
      const blob = await getCsv()
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = `${csvPrefix}-${new Date().toISOString().slice(0, 10)}.csv`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch {
      setExportError("The export could not be created. Please try again.")
    } finally {
      setIsExporting(false)
    }
  }

  const confirmDelete = () => {
    if (!pendingDelete) return
    const signup = pendingDelete
    deleteSignup.mutate(signup.email, {
      onSuccess: () => {
        toast({
          title: "Waitlist signup deleted",
          description: `${signup.email} was permanently removed.`,
        })
      },
      onError: () => {
        toast({
          title: "Could not delete signup",
          description: "The address was not removed. Please try again.",
          variant: "destructive",
        })
      },
    })
  }

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const signups = data?.signups ?? []

  return (
    <Layout>
      <div className="space-y-6">
        <CommsTabs />
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">{title}</h1>
            <p className="text-muted-foreground">
              {isLoading
                ? "Loading…"
                : `${data?.total ?? 0} ${data?.total === 1 ? "coach" : "coaches"} requested access.`}
            </p>
          </div>
          <Button onClick={exportCsv} disabled={isExporting || signups.length === 0}>
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
            {isExporting ? "Exporting…" : "Download CSV"}
          </Button>
        </div>

        <div className="rounded-lg border border-border bg-surface px-4 py-3 text-sm text-muted-foreground">
          Addresses are collected only for requested {purpose} and are automatically removed after 365
          days. The CSV export is capped at 5,000 newest signups.
        </div>

        {exportError && (
          <p role="alert" className="text-sm text-red-600">
            {exportError}
          </p>
        )}

        {isLoading ? (
          <div className="h-48 animate-pulse rounded-lg bg-muted" />
        ) : signups.length === 0 ? (
          <EmptyState
            icon={<EnvelopeIcon className="h-10 w-10" />}
            title={`No ${product} requests yet`}
            description="New landing-page requests will appear here."
          />
        ) : (
          <DataTable
            columns={columns}
            data={signups}
            searchKey="email"
            searchPlaceholder="Search loaded emails…"
            showPagination={false}
          />
        )}

        {data && data.total > signups.length && (
          <p className="text-sm text-muted-foreground">
            Showing the newest {signups.length} of {data.total}. Download the CSV for up to the
            newest 5,000 signups.
          </p>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
        title={`Delete ${product} signup?`}
        description={
          pendingDelete
            ? `${pendingDelete.email} will be permanently removed from ${listName}. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete signup"
        confirmVariant="destructive"
        onConfirm={confirmDelete}
      />
    </Layout>
  )
}
